import { useEffect, useMemo, useRef } from "react";
import { useQuery } from "@tanstack/react-query";
import { Swords } from "lucide-react";
import { getNationRivalry } from "@/lib/social.functions";
import { trackEvent } from "@/lib/audit";

const FLAGS: Record<string, string> = {
  MEX: "🇲🇽", ARG: "🇦🇷", BRA: "🇧🇷", FRA: "🇫🇷", ESP: "🇪🇸", USA: "🇺🇸",
  ENG: "🏴", GER: "🇩🇪", ITA: "🇮🇹", POR: "🇵🇹", URU: "🇺🇾", COL: "🇨🇴",
  JPN: "🇯🇵", KOR: "🇰🇷", NED: "🇳🇱", BEL: "🇧🇪", CRO: "🇭🇷", MAR: "🇲🇦",
};

type StormLevel = "fan_surge" | "fan_storm" | "nation_takeover" | "global_chaos";

function levelFor(total: number): StormLevel {
  if (total >= 1500) return "global_chaos";
  if (total >= 600) return "nation_takeover";
  if (total >= 200) return "fan_storm";
  return "fan_surge";
}

const LEVEL_META: Record<StormLevel, { label: string; color: string; emoji: string }> = {
  fan_surge:       { label: "Apoyo creciente", color: "oklch(0.78 0.14 220)", emoji: "🌊" },
  fan_storm:       { label: "Ola de apoyo",    color: "oklch(0.72 0.22 30)",  emoji: "⚡" },
  nation_takeover: { label: "Nación dominante", color: "oklch(0.78 0.2 60)",  emoji: "🔥" },
  global_chaos:    { label: "Locura mundial",  color: "oklch(0.62 0.28 20)",  emoji: "💥" },
};

function rivalryHeadline(
  top: { code: string; total: number; fans: number },
  second?: { code: string; total: number; fans: number },
): string {
  if (!second || second.total <= 0) {
    return `${top.code} lidera el orgullo de la afición`;
  }
  const ratio = top.total / Math.max(1, second.total);
  if (ratio > 2.2) return `${top.code} arrasa a ${second.code} en apoyo mundial`;
  if (ratio > 1.4) return `${top.code} supera a ${second.code} en apoyo`;
  if (Math.abs(top.total - second.total) < Math.max(20, top.total * 0.08))
    return `${top.code} vs ${second.code} pelean por el apoyo mundial`;
  return `${second.code} resiste el apoyo histórico de ${top.code}`;
}

/**
 * NationRivalryMomentsSurface — one dominant tribal beat.
 * Reuses getNationRivalry. Includes evolved Fan Storm level (FASE 3).
 */
export function NationRivalryMomentsSurface({ className = "" }: { className?: string }) {
  const q = useQuery({
    queryKey: ["social", "nation_rivalry"],
    queryFn: () => getNationRivalry(),
    refetchInterval: 30_000,
    staleTime: 20_000,
  });

  const rows = (q.data?.rows ?? []) as Array<{ nation_code: string; hype_total: number; active_fans: number }>;
  const ranked = useMemo(
    () =>
      rows
        .map((r) => ({
          code: String(r.nation_code),
          total: Number(r.hype_total ?? 0),
          fans: Number(r.active_fans ?? 0),
        }))
        .sort((a, b) => b.total - a.total),
    [rows],
  );

  const top = ranked[0];
  const second = ranked[1];
  const level = top ? levelFor(top.total) : null;
  const meta = level ? LEVEL_META[level] : null;
  const headline = top ? rivalryHeadline(top, second) : "";

  const lastLevel = useRef<StormLevel | null>(null);
  useEffect(() => {
    if (!level || lastLevel.current === level) return;
    lastLevel.current = level;
    void trackEvent("fan_storm_level_changed", { level, top: top?.code, total: top?.total ?? 0 });
  }, [level, top?.code, top?.total]);

  useEffect(() => {
    if (!top) return;
    void trackEvent("nation_rivalry_moment_shown", { top: top.code, second: second?.code, level });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [top?.code, second?.code, level]);

  if (!top || !meta) return null;

  return (
    <div
      className={`relative flex items-center gap-2 overflow-hidden rounded-full border border-border/60 bg-background/70 px-3 py-1.5 text-[11px] backdrop-blur-md ${className}`}
      style={{ boxShadow: `0 0 0 1px color-mix(in oklch, ${meta.color} 22%, transparent)` }}
      aria-label={`Nation rivalry: ${headline}`}
      data-storm-level={level ?? ""}
    >
      <span aria-hidden className="text-sm leading-none">{meta.emoji}</span>
      <span className="flex items-center gap-1 font-black uppercase tracking-[0.18em]" style={{ color: meta.color }}>
        <Swords className="h-3 w-3" />
        {meta.label}
      </span>
      <span className="mx-1 hidden text-muted-foreground sm:inline">·</span>
      <span className="truncate text-foreground/90">
        <span aria-hidden className="mr-1">{FLAGS[top.code] ?? "🏳️"}</span>
        {headline}
      </span>
    </div>
  );
}
